import { makeStateStore } from '@playcastdotio/StateStore';
import type { HostClassification } from '@playcastdotio/CoreTypes';
import { PlaycastAnalyticsRole } from './types';

/**
 * Analytics Identity Store
 *
 * Provides default values for common analytics identifiers based on role.
 * Values set here are automatically applied to analytics events unless explicitly overridden.
 *
 * This allows centralized management of identity (especially important for guest mode)
 * without requiring every analytics call to explicitly pass these values.
 */

type HostIdentity = {
  hostAuthId?: string;
  machineId?: string;
  persistentId?: string;
  /**
   * Analytics segment for this machine, resolved by the server at WebSocket auth
   * and delivered in AUTH_SUCCESS. Left unset when the server does not send it,
   * so events are absent the field rather than mislabelled.
   */
  hostClassification?: HostClassification;
};

type PlayerIdentity = {
  playerAuthId?: string;
  persistentId?: string;
};

type AnalyticsIdentityState = {
  host: HostIdentity;
  player: PlayerIdentity;
  spectator: Record<string, never>;
  celebrity: Record<string, never>;
  system: Record<string, never>;
};

const initialState: AnalyticsIdentityState = {
  host: {},
  player: {},
  spectator: {},
  celebrity: {},
  system: {},
};

export const analyticsIdentityStore = makeStateStore<AnalyticsIdentityState>(initialState);

/**
 * Set analytics identity for a specific role
 * Values will be automatically applied to future analytics events for that role
 * Only updates if values have actually changed (efficient)
 */
// Overload for host
export function setAnalyticsIdentity(role: 'host', data: Partial<HostIdentity>): void;
// Overload for player
export function setAnalyticsIdentity(role: 'player', data: Partial<PlayerIdentity>): void;
// Implementation
export function setAnalyticsIdentity(
  role: PlaycastAnalyticsRole,
  data: Partial<HostIdentity | PlayerIdentity>
): void {
  const currentState = analyticsIdentityStore.currentState;
  const currentRoleIdentity = currentState[role as 'host' | 'player'];

  // Check if any values have actually changed
  let hasChanges = false;
  for (const key in data) {
    if ((data as Record<string, unknown>)[key] !== (currentRoleIdentity as Record<string, unknown>)[key]) {
      hasChanges = true;
      break;
    }
  }

  // Skip update if no changes
  if (!hasChanges) {
    return;
  }

  analyticsIdentityStore.updateState({
    ...currentState,
    [role]: {
      ...currentRoleIdentity,
      ...data,
    },
  });
  console.log(`[Analytics Identity] Updated ${role} identity:`, data);
}

/**
 * Get analytics identity for a specific role
 * Returns empty object if no identity is set
 */
// Overload for host
export function getAnalyticsIdentity(role: 'host'): HostIdentity;
// Overload for player
export function getAnalyticsIdentity(role: 'player'): PlayerIdentity;
// Overload for other roles
export function getAnalyticsIdentity(role: PlaycastAnalyticsRole): Record<string, never>;
// Implementation
export function getAnalyticsIdentity(role: PlaycastAnalyticsRole): HostIdentity | PlayerIdentity | Record<string, never> {
  if (role === 'host') {
    return analyticsIdentityStore.currentState.host || {};
  }
  if (role === 'player') {
    return analyticsIdentityStore.currentState.player || {};
  }
  return {};
}

/**
 * Clear analytics identity for a specific role
 */
export const clearAnalyticsIdentity = (role: PlaycastAnalyticsRole) => {
  const currentState = analyticsIdentityStore.currentState;
  analyticsIdentityStore.updateState({
    ...currentState,
    [role]: {},
  });
  console.log(`[Analytics Identity] Cleared ${role} identity`);
};

